if (!customElements.get('wb-article-commerce')) {
  customElements.define('wb-article-commerce', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.isSubmitting = false;
      this.floatingBar = this.querySelector('[data-article-floating]');
      this.productsArea = this.querySelector('[data-article-products]');
      const options = { signal: this.controller.signal };
      this.addEventListener('submit', (event) => this.addToCart(event), options);
      this.querySelector('[data-article-scroll]')?.addEventListener('click', (event) => {
        event.preventDefault();
        const targetId = event.currentTarget.getAttribute('href')?.slice(1);
        const target = document.getElementById(targetId);
        if (!target || !this.contains(target)) return;
        const focusTarget = target.querySelector('[data-article-main-title]') || target;
        const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        focusTarget.focus({ preventScroll: true });
        target.scrollIntoView({ behavior: isReducedMotion ? 'instant' : 'smooth', block: 'start' });
      }, options);
      if (!this.floatingBar) return;
      this.scheduleUpdate = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateFloating();
        });
      };
      window.addEventListener('scroll', this.scheduleUpdate, { ...options, passive: true });
      window.addEventListener('resize', this.scheduleUpdate, options);
      document.addEventListener('toggle', this.scheduleUpdate, { ...options, capture: true });
      this.overlayObserver = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => !this.contains(mutation.target))) this.scheduleUpdate();
      });
      this.overlayObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class', 'open', 'aria-hidden', 'aria-modal'] });
      this.updateFloating();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.overlayObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.controller = null;
      this.frame = null;
    }

    updateFloating() {
      if (!this.isConnected || !this.floatingBar || !this.productsArea) return;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const overlays = document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]:not([aria-hidden="true"]), cart-drawer.active, cart-notification.active, #wondercare-root .support-panel[aria-hidden="false"]');
      const isOverlayOpen = [...overlays].some((element) => !element.hidden && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
      const launcher = document.querySelector('#wondercare-root .help-launcher');
      const launcherRect = launcher?.getBoundingClientRect();
      const isLauncherVisible = launcherRect?.width > 0 && getComputedStyle(launcher).visibility !== 'hidden';
      const floatingRight = isLauncherVisible ? Math.max(12, window.innerWidth - launcherRect.left + 8) : 12;
      this.style.setProperty('--wb-article-floating-right', `${floatingRight}px`);
      this.floatingBar.hidden = !isMobile || this.isSubmitting || isOverlayOpen || window.scrollY < window.innerHeight || this.productsArea.getBoundingClientRect().top <= window.innerHeight;
    }

    async addToCart(event) {
      const form = event.target.closest('[data-article-cart-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();
      if (this.isSubmitting) return;
      const button = form.querySelector('[type="submit"]');
      if (!button || button.disabled) return;
      const card = form.closest('[data-article-product]');
      const error = card.querySelector('[role="alert"]');
      const label = button.querySelector('[data-article-button-label]');
      const originalLabel = label.textContent;
      const formData = new FormData(form);
      const variantId = formData.get('id');
      const buttons = [...this.querySelectorAll('[data-article-cart-form] [type="submit"]')];
      this.isSubmitting = true;
      error.hidden = true;
      error.textContent = '';
      buttons.forEach((item) => { item.disabled = true; });
      label.textContent = this.dataset.addingLabel;
      form.setAttribute('aria-busy', 'true');
      this.updateFloating();
      let errorMessage = this.dataset.errorLabel;
      try {
        const addUrl = new URL(this.dataset.cartAddUrl, window.location.origin);
        if (!addUrl.pathname.endsWith('.js')) addUrl.pathname += '.js';
        const response = await fetch(addUrl.toString(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          if (typeof data.description === 'string') errorMessage = data.description;
          throw new Error(errorMessage);
        }
        const addedItem = Array.isArray(data.items) ? data.items.find((item) => String(item.variant_id || item.id) === String(variantId)) : null;
        if (!addedItem || addedItem.quantity < 1) throw new Error(this.dataset.errorLabel);
        window.location.assign(this.dataset.cartUrl);
      } catch {
        if (!this.isConnected) return;
        error.textContent = errorMessage;
        error.hidden = false;
        buttons.forEach((item) => { item.disabled = false; });
        label.textContent = originalLabel;
        form.removeAttribute('aria-busy');
        this.isSubmitting = false;
        this.updateFloating();
      }
    }
  });
}
